import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { motion } from 'framer-motion';
import Select from 'react-select';

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler, ChartDataLabels);

/**
 * CategoryTrends Component
 * 
 * Enhanced visualization of spending by category with year selection
 * and collapsible monthly breakdowns.
 */

// Custom CSS for hiding scrollbars
const noScrollbarStyle = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  /* For smoother collapsible transitions */
  .collapsible-content {
    transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), 
                opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }
  
  .collapsible-content.open {
    max-height: 2000px;
    opacity: 1;
  }
  
  .collapsible-content.closed {
    max-height: 0;
    opacity: 0;
  }
  
  .chevron-rotate {
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .chevron-rotate.open {
    transform: rotate(180deg);
  }
`;

function CategoryTrends({ summaryData }) {
  // Add the style tag inside the component
  useEffect(() => {
    // Add style tag for the no-scrollbar class
    const styleTag = document.createElement('style');
    styleTag.innerHTML = noScrollbarStyle;
    document.head.appendChild(styleTag);
    
    // Cleanup when component unmounts
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const [allCategories, setAllCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYearForMonthly, setSelectedYearForMonthly] = useState(null);
  const [openYearSections, setOpenYearSections] = useState({});
  const currentYear = new Date().getFullYear();

  const fetchAllCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const startYear = 2020;
      const years = Array.from({ length: currentYear - startYear }, (_, i) => startYear + i); // Exclude current year
      const yearPromises = years.map(year =>
        // Use new efficient endpoint for yearly data
        axios.get(`/api/yearly-data/${year}`, { 
          withCredentials: true,
          timeout: 10000,
        })
          .then(res => res.data || { total: 0, income: 0, byCategory: {}, months: {} })
          .catch(() => {
            // Fall back to legacy endpoint if needed
            return axios.get(`/api/yearly-summaries?year=${year}`, { 
              withCredentials: true,
              timeout: 10000,
            })
              .then(res => res.data || { total: 0, income: 0, byCategory: {}, months: {} })
              .catch(() => ({ total: 0, income: 0, byCategory: {}, months: {} }));
          })
      );
      const results = await Promise.all(yearPromises);
      const categoriesSet = new Set();
      results.forEach(yearData => {
        Object.keys(yearData.byCategory || {}).forEach(cat => categoriesSet.add(cat));
      });
      setAllCategories([...categoriesSet].sort().map(cat => ({ value: cat, label: cat })));
    } catch {
      setAllCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchAllCategories();
  }, [fetchAllCategories]);

  const fetchCategoryHistory = useCallback(async (category) => {
    if (!category) return;
    setIsLoading(true);
    try {
      const startYear = 2020;
      const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);
      const yearPromises = years.map(year =>
        // Use new efficient endpoint for yearly data
        axios.get(`/api/yearly-data/${year}`, { 
          withCredentials: true,
          timeout: 10000,
        })
          .then(res => ({ year, data: res.data || { total: 0, income: 0, byCategory: {}, months: {} } }))
          .catch(() => {
            // Fall back to legacy endpoint if needed
            return axios.get(`/api/yearly-summaries?year=${year}`, { 
              withCredentials: true,
              timeout: 10000,
            })
              .then(res => ({ year, data: res.data || { total: 0, income: 0, byCategory: {}, months: {} } }))
              .catch(() => ({ year, data: { total: 0, income: 0, byCategory: {}, months: {} } }));
          })
      );
      const results = await Promise.all(yearPromises);
      const dataByYear = results.reduce((acc, { year, data }) => {
        acc[year] = { 
          total: data.byCategory[category] || 0, 
          // Includes the live (unfinalized) current month, which the backend now merges in
          months: Object.fromEntries(
            Object.entries(data.months || {})
              .map(([month, monthData]) => [month, monthData.byCategory?.[category] || 0])
          )
        };
        return acc;
      }, {});
      setCategoryData(dataByYear);
      
      // Set the default selected year to the most recent year with data
      const yearsWithData = Object.keys(dataByYear)
        .filter(year => Object.values(dataByYear[year].months).some(val => val > 0))
        .map(Number);
      
      if (yearsWithData.length > 0) {
        setSelectedYearForMonthly(Math.max(...yearsWithData));
      } else {
        setSelectedYearForMonthly(currentYear);
      }
    } catch {
      setCategoryData({});
    } finally {
      setIsLoading(false);
    }
  }, [currentYear]);

  const handleCategoryChange = (option) => {
    setSelectedCategory(option);
    setCategoryData({});
    fetchCategoryHistory(option?.value);
  };

  // Functions for collapsible sections
  const toggleYearCollapse = (year) => {
    setOpenYearSections(prev => {
      // Create a new object where all years are closed
      const newState = Object.keys(prev).reduce((acc, yearKey) => {
        acc[yearKey] = false;
        return acc;
      }, {});
      
      // Toggle the target year's state
      newState[year] = !prev[year];
      return newState;
    });
  };

  const isYearOpen = (year) => openYearSections[year] || false;

  const yearlyBarData = useMemo(() => {
    const years = Object.keys(categoryData).sort();
    const data = years.map(year => categoryData[year].total || 0);
    return {
      labels: years.length > 0 ? years : ['No Data'],
      datasets: [{
        label: selectedCategory?.value || 'No Category',
        data: years.length > 0 ? data : [0],
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return 'rgba(139, 92, 246, 0.8)';
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(139, 92, 246, 0.9)');
          gradient.addColorStop(0.5, 'rgba(99, 102, 241, 0.8)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0.7)');
          return gradient;
        },
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return 'rgba(139, 92, 246, 1)';
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(139, 92, 246, 1)');
          gradient.addColorStop(0.5, 'rgba(99, 102, 241, 1)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 1)');
          return gradient;
        },
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3
      }],
    };
  }, [categoryData, selectedCategory]);

  // Yearly percentage change data
  const yearlyPercentChangeData = useMemo(() => {
    const years = Object.keys(categoryData).sort();
    if (years.length < 2) return { labels: ['No Data'], datasets: [{ data: [0] }] };
    
    const values = years.map(year => categoryData[year].total || 0);
    const percentChanges = values.map((val, idx) => {
      if (idx === 0) return 0;
      const prevVal = values[idx - 1];
      if (prevVal === 0) return 0;
      return ((val - prevVal) / prevVal) * 100;
    });

    return {
      labels: years,
      datasets: [{
        label: `${selectedCategory?.value || 'No Category'} YoY % Change`,
        data: percentChanges,
        backgroundColor: percentChanges.map((val, index) => {
          return (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return val > 0 ? 'rgba(16, 185, 129, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            if (val > 0) {
              gradient.addColorStop(0, 'rgba(16, 185, 129, 0.9)');
              gradient.addColorStop(0.5, 'rgba(34, 197, 94, 0.8)');
              gradient.addColorStop(1, 'rgba(22, 163, 74, 0.7)');
            } else {
              gradient.addColorStop(0, 'rgba(239, 68, 68, 0.9)');
              gradient.addColorStop(0.5, 'rgba(220, 38, 38, 0.8)');
              gradient.addColorStop(1, 'rgba(185, 28, 28, 0.7)');
            }
            return gradient;
          };
        }),
        borderColor: percentChanges.map(val => 
          val > 0 ? 'rgba(16, 185, 129, 1)' : 'rgba(239, 68, 68, 1)'
        ),
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: percentChanges.map(val => 
          val > 0 ? 'rgba(16, 185, 129, 1)' : 'rgba(239, 68, 68, 1)'
        ),
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3
      }],
    };
  }, [categoryData, selectedCategory]);

  // Cumulative spending data
  const cumulativeSpendData = useMemo(() => {
    const years = Object.keys(categoryData).sort();
    if (years.length === 0) return { labels: ['No Data'], datasets: [{ data: [0] }] };
    
    const values = years.map(year => categoryData[year].total || 0);
    const cumulativeValues = [];
    let sum = 0;
    
    for (const val of values) {
      sum += val;
      cumulativeValues.push(sum);
    }

    return {
      labels: years,
      datasets: [{
        label: `${selectedCategory?.value || 'No Category'} Cumulative Spend`,
        data: cumulativeValues,
        borderColor: 'rgba(124, 58, 237, 1)',
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(124, 58, 237, 0.4)');
          gradient.addColorStop(0.5, 'rgba(139, 92, 246, 0.2)');
          gradient.addColorStop(1, 'rgba(168, 85, 247, 0.05)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: 'rgba(124, 58, 237, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: 'rgba(124, 58, 237, 1)',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3
      }],
    };
  }, [categoryData, selectedCategory]);
  // Top months data
  const topMonthsData = useMemo(() => {
    const allMonthsData = Object.entries(categoryData).flatMap(([year, yearData]) =>
      Object.entries(yearData.months).map(([month, value]) => ({
        label: `${month.slice(0, 3)} ${year}`,
        value,
        year: parseInt(year),
        month
      }))
    ).filter(item => item.value > 0);
    
    if (allMonthsData.length === 0) return { labels: ['No Data'], datasets: [{ data: [0] }] };
    
    // Sort by value descending and take top 10
    const topMonths = [...allMonthsData].sort((a, b) => b.value - a.value).slice(0, 10);
    
    return {
      labels: topMonths.map(item => item.label),
      datasets: [{
        label: `${selectedCategory?.value || 'No Category'} Top Months`,
        data: topMonths.map(item => item.value),
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return 'rgba(245, 158, 11, 0.8)';
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(245, 158, 11, 0.9)');
          gradient.addColorStop(0.5, 'rgba(251, 191, 36, 0.8)');
          gradient.addColorStop(1, 'rgba(252, 211, 77, 0.7)');
          return gradient;
        },
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return 'rgba(245, 158, 11, 1)';
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(245, 158, 11, 1)');
          gradient.addColorStop(0.5, 'rgba(251, 191, 36, 1)');
          gradient.addColorStop(1, 'rgba(252, 211, 77, 1)');
          return gradient;
        },
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3
      }],
    };
  }, [categoryData, selectedCategory]);

  const monthlyLineData = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Allow selecting any year that has data
    const yearsWithData = Object.keys(categoryData)
      .filter(year => Object.values(categoryData[year].months).some(val => val > 0))
      .map(Number)
      .sort((a, b) => b - a); // Sort descending

    const yearData = categoryData[selectedYearForMonthly] || {};
    const data = months.map(month => yearData.months?.[month] || 0);
    
    return {
      labels: months.map(m => m.slice(0, 3)),
      datasets: [{
        label: `${selectedCategory?.value || 'No Category'} (${selectedYearForMonthly})`,
        data,
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
          gradient.addColorStop(0.5, 'rgba(34, 197, 94, 0.2)');
          gradient.addColorStop(1, 'rgba(22, 163, 74, 0.05)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3
      }],
      yearsWithData
    };
  }, [categoryData, selectedCategory, selectedYearForMonthly]);

  const monthlyBreakdown = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndices = months.reduce((acc, month, index) => {
      acc[month] = index;
      return acc;
    }, {});

    // Get all unique years
    const years = [...new Set(
      Object.entries(categoryData).flatMap(([year, data]) =>
        Object.keys(data.months).length > 0 ? [parseInt(year)] : []
      )
    )].sort((a, b) => b - a); // Sort descending

    // Prepare the table data structure
    const tableData = years.map(year => {
      const monthsData = months.map(month => ({
        month,
        amount: categoryData[year]?.months[month] || 0,
        // Get the previous month's amount for trend calculation
        prevAmount: month === 'January' 
          ? null 
          : categoryData[year]?.months[months[monthIndices[month] - 1]] || 0
      }));
      
      return {
        year,
        months: monthsData
      };
    });

    return tableData;
  }, [categoryData]);

  // Initialize the first year as open when data changes
  useEffect(() => {
    if (monthlyBreakdown.length > 0) {
      const mostRecentYear = monthlyBreakdown[0].year;
      setOpenYearSections({ [mostRecentYear]: true });
    }
  }, [monthlyBreakdown]);

  const getTrendIndicator = (current, previous) => {
    if (previous === null || (current === 0 && previous === 0)) return null;
    
    const percentChange = previous === 0 
      ? (current > 0 ? 100 : 0) 
      : ((current - previous) / previous) * 100;
    
    if (Math.abs(percentChange) < 5) return "neutral";
    return percentChange > 0 ? "up" : "down";
  };

  const getTrendColor = (trend) => {
    if (trend === "up") return "text-red-500";
    if (trend === "down") return "text-green-500";
    return "text-gray-400";
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") {
      return (
        <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
        </svg>
      );
    }
    if (trend === "down") {
      return (
        <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14"></path>
      </svg>
    );
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      borderRadius: '0.5rem',
      borderColor: state.isFocused ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
      padding: '0.25rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 255, 255, 0.2)' : 'none',
      '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      transition: 'all 0.2s ease-in-out',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'rgba(255, 255, 255, 0.2)' 
        : state.isFocused 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'transparent',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white' },
      padding: '0.75rem 1rem',
      transition: 'background-color 0.15s ease-in-out',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      borderRadius: '0.5rem',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      width: '100%',
      zIndex: 9999,
      position: 'relative'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: 'fixed'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0.25rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white',
      fontWeight: 500,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': { color: 'white' },
      transition: 'color 0.15s ease-in-out',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': { color: 'white' },
      transition: 'color 0.15s ease-in-out',
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'top', 
        labels: { 
          boxWidth: 12, 
          usePointStyle: true,
          font: {
            family: "'Inter', 'system-ui', sans-serif",
            size: 12,
            weight: 500,
          },
          color: 'rgba(255, 255, 255, 0.9)',
          padding: 15,
        },
      },
      tooltip: { 
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        titleFont: {
          family: "'Inter', 'system-ui', sans-serif",
          size: 13,
          weight: 600,
        },
        bodyFont: {
          family: "'Inter', 'system-ui', sans-serif",
          size: 12,
        },
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 12,
        displayColors: true,
        callbacks: { 
          label: (ctx) => `${ctx.dataset.label}: ₪${ctx.raw.toLocaleString('en-US')}` 
        } 
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      y: { 
        beginAtZero: true, 
        grid: { 
          color: 'rgba(255, 255, 255, 0.1)',
          tickBorderDash: [2, 4],
        }, 
        border: {
          display: false,
        },
        ticks: { 
          callback: (val) => `₪${val.toLocaleString('en-US')}`,
          font: {
            family: "'Inter', 'system-ui', sans-serif",
            size: 11,
          },
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 8,
        } 
      },
      x: { 
        grid: { display: false },
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: "'Inter', 'system-ui', sans-serif",
            size: 11,
          },
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 8,
        }
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeOutQuart',
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };
  const percentChangeOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: {
          callback: (val) => `${val.toFixed(1)}%`,
          font: {
            family: "'Inter', 'system-ui', sans-serif",
            size: 11,
          },
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 8,
        }
      }
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        ...chartOptions.plugins.tooltip,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(1)}%`
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeOutQuart'
    }
  };

  return (
    <motion.div 
      className="min-h-screen p-4 md:p-6 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="w-full max-w-7xl mx-auto">
        <header className="mb-8 bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Category Trends</h1>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">Analyze historical spending patterns and track how your expenses evolve over time for specific categories.</p>
          <div className="inline-block w-full max-w-md">
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={allCategories}
              styles={customStyles}
              placeholder="Select a spending category to analyze"
              className="w-full"
              classNamePrefix="react-select"
              isDisabled={isLoading}
              menuPortalTarget={document.getElementById('dropdown-portal')}
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
            />
          </div>
        </header>

        {isLoading ? (
          <motion.div 
            className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-12 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-center items-center h-64">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full border-4 border-white/50 border-t-transparent animate-spin mb-6"></div>
                <p className="text-white font-medium">Loading {selectedCategory ? `${selectedCategory.value} trends` : 'categories'}...</p>
                <p className="text-white/60 text-sm mt-2">This may take a moment</p>
              </div>
            </div>
          </motion.div>
        ) : selectedCategory ? (
          Object.keys(categoryData).length > 0 && Object.values(categoryData).some(data => data.total > 0) ? (
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
{/* Monthly Trends with Year Selector */}
<motion.div 
  className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl"
  initial={{ opacity: 0, x: -20 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6, delay: 0.3 }}
>
  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
    <h3 className="text-lg font-semibold text-white flex items-center">
      <svg className="w-5 h-5 mr-2 text-emerald-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M3 3a1 1 0 000 2h10a1 1 0 100-2H3zm0 4a1 1 0 000 2h6a1 1 0 100-2H3zm0 4a1 1 0 100 2h12a1 1 0 100-2H3z" clipRule="evenodd"></path>
      </svg>
      {selectedCategory.value} - Monthly Trends
    </h3>
    {monthlyLineData.yearsWithData && monthlyLineData.yearsWithData.length > 0 && (
      <div className="overflow-x-auto pb-1 -mx-1 px-1">
        <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-lg shadow-sm border border-white/20 p-1 whitespace-nowrap">
          {monthlyLineData.yearsWithData.map(year => (
            <button
              key={year}
              onClick={() => setSelectedYearForMonthly(year)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                selectedYearForMonthly === year
                  ? 'bg-white/20 text-white shadow-sm ring-1 ring-white/30'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
  <div className="chart-container" style={{ position: 'relative', zIndex: 1 }}>
    <Line
      data={monthlyLineData}
      options={chartOptions}
      height={100}
    />
  </div>
</motion.div>
              <motion.div 
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {/* Yearly Totals */}
                <motion.div 
                  className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
                      <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
                    </svg>
                    {selectedCategory.value} - Yearly Totals
                  </h3>
                  <div className="chart-container" style={{ position: 'relative', zIndex: 1 }}>
                    <Bar
                      data={yearlyBarData}
                      options={chartOptions}
                      height={220}
                    />
                  </div>
                </motion.div>

                {/* Year-over-Year % Change */}
                <motion.div 
                  className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"></path>
                    </svg>
                    {selectedCategory.value} - Year-over-Year % Change
                  </h3>
                  <div className="chart-container" style={{ position: 'relative', zIndex: 1 }}>
                    <Bar
                      data={yearlyPercentChangeData}
                      options={percentChangeOptions}
                      height={220}
                    />
                  </div>
                </motion.div>

                {/* Cumulative Spend */}
                <motion.div 
                  className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-violet-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"></path>
                    </svg>
                    {selectedCategory.value} - Cumulative Spend
                  </h3>
                  <div className="chart-container" style={{ position: 'relative', zIndex: 1 }}>
                    <Line
                      data={cumulativeSpendData}
                      options={chartOptions}
                      height={220}
                    />
                  </div>
                </motion.div>
                {/* Top Spending Months */}
                <motion.div 
                  className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-amber-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd"></path>
                    </svg>
                    {selectedCategory.value} - Top Spending Months
                  </h3>
                  <div className="chart-container" style={{ position: 'relative', zIndex: 1 }}>
                    <Bar
                      data={topMonthsData}
                      options={chartOptions}
                      height={220}
                    />
                  </div>
                </motion.div>
              </motion.div>

              {/* Monthly Breakdown Table (Collapsible) */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl">
                <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                  {selectedCategory.value} - Monthly Breakdown by Year
                </h3>

                {monthlyBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    {monthlyBreakdown.map((yearData) => (
                     <div key={yearData.year} className="border border-white/20 rounded-xl overflow-hidden mb-4 last:mb-0">
                     <button 
                       onClick={() => toggleYearCollapse(yearData.year)}
                       className="flex w-full justify-between items-center py-3 px-4 bg-white/10 hover:bg-white/20 transition-colors duration-200 text-left"
                     >
                       <h4 className="text-lg font-medium text-white flex items-center">
                         <span className="mr-2">{yearData.year}</span>
                         <span className="text-sm font-normal text-white/70 ml-2">
                           (Total: ₪{yearData.months.reduce((sum, month) => sum + month.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                         </span>
                       </h4>
                       <svg 
                         className={`w-5 h-5 text-white/70 chevron-rotate ${isYearOpen(yearData.year) ? 'open' : ''}`} 
                         fill="none" 
                         stroke="currentColor" 
                         viewBox="0 0 24 24" 
                         xmlns="http://www.w3.org/2000/svg"
                       >
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                       </svg>
                     </button>
                     
                     <div className={`collapsible-content ${isYearOpen(yearData.year) ? 'open' : 'closed'}`}>
                       {/* Keep your existing table content here */}
                          <table className="w-full text-sm text-white/80 border-collapse">
                            <thead>
                              <tr className="border-b border-white/20 bg-white/5">
                                <th className="py-3 px-4 text-left font-semibold text-white">Month</th>
                                <th className="py-3 px-4 text-right font-semibold text-white">Amount (₪)</th>
                                <th className="py-3 px-4 text-center font-semibold text-white">Trend</th>
                              </tr>
                            </thead>
                            <tbody>
                              {yearData.months.map((monthData) => {
                                const trend = getTrendIndicator(monthData.amount, monthData.prevAmount);
                                const trendColor = getTrendColor(trend);
                                return (
                                  <tr key={monthData.month} className="border-b border-white/10 hover:bg-white/5 transition-colors duration-150">
                                    <td className="py-3 px-4 font-medium text-white/90">{monthData.month}</td>
                                    <td className="py-3 px-4 text-right font-medium text-white/90">
                                      {monthData.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={`py-3 px-4 text-center ${trendColor === 'text-red-500' ? 'text-red-400' : trendColor === 'text-green-500' ? 'text-green-400' : 'text-white/60'}`}>
                                      {getTrendIcon(trend)}
                                      {trend === 'up' && ' ▲'}
                                      {trend === 'down' && ' ▼'}
                                      {trend === 'neutral' && ' ◆'}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-white/10">
                                <td className="py-3 px-4 font-semibold text-white">Year Total</td>
                                <td className="py-3 px-4 text-right font-semibold text-white">
                                  {yearData.months.reduce((sum, month) => sum + month.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-3 px-4"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-white/30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <h3 className="text-xl font-medium text-white mb-2">No Data Available</h3>
                    <p className="text-white/70">No monthly data found for {selectedCategory.value}.</p>
                  </div>
                )}
              </div>
            </motion.div>
            ) : (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-12 text-center">
              <div className="w-20 h-20 mx-auto text-white/20 mb-6 bg-white/10 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">No Data Available</h3>
              <p className="text-white/70 max-w-md mx-auto">No historical spending data found for {selectedCategory.value}. This category might be new or data is still being processed.</p>
              <button 
                onClick={() => fetchCategoryHistory(selectedCategory.value)}
                className="mt-6 inline-flex items-center px-4 py-2 border border-white/30 text-sm font-medium rounded-md shadow-sm text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/30 transition-colors duration-150 backdrop-blur-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh Data
              </button>
            </div>
          )
        ) : (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-12 text-center">
            <div className="w-20 h-20 mx-auto text-white/20 mb-6 bg-white/10 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </div>
            <h3 className="text-xl font-medium text-white mb-3">Select a Category</h3>
            <p className="text-white/70 max-w-md mx-auto">Choose a spending category from the dropdown above to view its historical trends and detailed breakdown.</p>
            {allCategories.length > 0 && (
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                <p className="w-full text-sm text-white/60 mb-2">Quick access categories:</p>
                {['Shopping', 'Charity', 'Children', 'Cleaner', 'Therapists'].map(categoryName => {
                  const category = allCategories.find(cat => cat.label === categoryName);
                  return category ? (
                    <button
                      key={category.value}
                      onClick={() => handleCategoryChange(category)}
                      className="px-4 py-2 bg-white/20 text-white rounded-full text-sm font-medium hover:bg-white/30 transition-colors duration-150 backdrop-blur-sm"
                    >
                      {category.label}
                    </button>
                  ) : (
                    <button
                      key={categoryName}
                      onClick={() => {}}
                      className="px-4 py-2 bg-white/10 text-white/40 rounded-full text-sm font-medium cursor-not-allowed"
                      disabled
                      title="Category not available"
                    >
                      {categoryName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default CategoryTrends;